/**
 * WebMCP — W3C standard (Chrome 146+ preview) that exposes site capabilities
 * as structured tools to AI agents via navigator.modelContext.
 *
 * Fully progressive: no-ops silently in browsers that don't support it.
 * Spec: https://webmcp.link/
 */

import { blogPosts } from "$lib/data/blogPosts";
import { goto } from "$app/navigation";

// navigator.modelContext is not yet in TypeScript's lib.dom.d.ts
declare global {
  interface Navigator {
    modelContext?: {
      registerTool(definition: WebMCPTool): void;
    };
  }
}

interface WebMCPTool {
  name: string;
  description: string;
  readOnly?: boolean;
  inputSchema: Record<string, unknown>;
  execute(params: Record<string, unknown>): Promise<unknown>;
}

export function registerWebMCP(): void {
  if (!navigator.modelContext) return;

  const mc = navigator.modelContext;

  mc.registerTool({
    name: "searchBlogPosts",
    description:
      "Search dinglandia blog posts by keyword, tag, or both. Returns matching post metadata including slug, title, description, date, tags, and read time.",
    readOnly: true,
    inputSchema: {
      type: "object",
      properties: {
        query: {
          type: "string",
          description: "Text to search across title, description, and tags",
        },
        tag: {
          type: "string",
          description: "Filter by an exact tag name (case-insensitive)",
        },
        limit: {
          type: "number",
          description: "Maximum number of results to return (default 10)",
        },
      },
    },
    async execute({ query, tag, limit = 10 }) {
      let results = [...blogPosts];

      if (typeof query === "string" && query.trim()) {
        const q = query.toLowerCase().trim();
        results = results.filter((p) =>
          [p.title, p.description, ...p.tags]
            .join(" ")
            .toLowerCase()
            .includes(q),
        );
      }

      if (typeof tag === "string" && tag.trim()) {
        const t = tag.toLowerCase().trim();
        results = results.filter((p) =>
          p.tags.some((pt) => pt.toLowerCase() === t),
        );
      }

      const n = typeof limit === "number" ? Math.min(limit, 50) : 10;
      return results
        .slice(0, n)
        .map(({ slug, title, description, date, tags, readTime }) => ({
          slug,
          title,
          description,
          date,
          tags,
          readTime,
          url: `/posts/${slug}/`,
        }));
    },
  });

  mc.registerTool({
    name: "getBlogPost",
    description:
      "Get full metadata for a specific dinglandia blog post by its slug.",
    readOnly: true,
    inputSchema: {
      type: "object",
      required: ["slug"],
      properties: {
        slug: {
          type: "string",
          description: "The post slug (URL path identifier)",
        },
      },
    },
    async execute({ slug }) {
      const post = blogPosts.find((p) => p.slug === slug);
      if (!post) return { error: `No post found with slug "${slug}"` };
      return { ...post, url: `/posts/${slug}/` };
    },
  });

  mc.registerTool({
    name: "listBlogTags",
    description:
      "List all unique tags used across dinglandia blog posts, sorted by post count descending.",
    readOnly: true,
    inputSchema: {
      type: "object",
      properties: {},
    },
    async execute() {
      const counts: Record<string, number> = {};
      blogPosts.forEach((p) =>
        p.tags.forEach((t) => {
          counts[t] = (counts[t] ?? 0) + 1;
        }),
      );
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .map(([tag, count]) => ({ tag, count }));
    },
  });

  mc.registerTool({
    name: "navigateTo",
    description: "Navigate the browser to a named page on the dinglandia site.",
    inputSchema: {
      type: "object",
      required: ["page"],
      properties: {
        page: {
          type: "string",
          enum: [
            "home",
            "blog",
            "work",
            "mission",
            "services",
            "process",
            "contact",
          ],
          description: "The destination page name",
        },
      },
    },
    async execute({ page }) {
      const routes: Record<string, string> = {
        home: "/",
        blog: "/blog",
        work: "/work",
        mission: "/mission",
        services: "/services",
        process: "/process",
        contact: "/contact",
      };
      const path = routes[page as string];
      if (!path) return { error: `Unknown page "${page}"` };
      await goto(path);
      return { navigated: true, path };
    },
  });
}
